/// <amd-module name="Repos/Views/Settings/Repositories/Components/RepositoriesDetailsPanel" />
import React = require("react");
import { FeatureFlagConstants, ResourceTypeNames } from "Checks/ChecksOverview/Generated/Constants";
import { GitRepositoryPermissions } from "Repos/Common/Permissions";
import { refToBranchOrTagName } from "Repos/Common/Util/Ref";
import { ISettingsViewService, SettingsTabIds } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { DialogType, IRepository } from "Repos/Views/Settings/Repositories/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories/Resources";
import { IRepositoriesViewService, permissionsAdditionalData } from "Repos/Views/Settings/Repositories/Services/RepositoriesViewService";
import { IObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tab, TabBar } from "VSSUI/Tabs";
import { RepositoriesChecksComponent } from "./RepositoriesChecksComponent";
import { RepositoryPermissions } from "./RepositoryPermissions";
import { RepositoryPolicies } from "./RepositoryPolicies";

interface IRepositoriesDetailsPanelProps {
    data: IRepository | string;
    createChildLayer?(): void;
}

export class RepositoriesDetailsPanel extends VssComponent<IRepositoriesDetailsPanelProps> {
    private protectedReposFlag: boolean;
    private scrollRef: React.RefObject<HTMLDivElement>;
    private selectedRepository: IReadonlyObservableValue<IRepository | undefined>;
    private selectedRefs: IReadonlyObservableValue<string | undefined>;
    private selectedTabId: IReadonlyObservableValue<string | undefined>;
    private svc: IRepositoriesViewService;
    private showApprovalsAndChecks = new ObservableValue(false);
    private showCheckIssueMessageCard: boolean;
    private repositoryPermissions: ObservableValue<{ [key in GitRepositoryPermissions]?: boolean }> = new ObservableValue({});
    private checkedPermissions = [
        GitRepositoryPermissions.RenameRepository,
        GitRepositoryPermissions.EditPolicies,
        GitRepositoryPermissions.DeleteRepository
    ];

    public constructor(props: IRepositoriesDetailsPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.scrollRef = React.createRef<HTMLDivElement>();
        this.svc = context.pageContext.getService<IRepositoriesViewService>("RepositoriesViewService");
        this.selectedRepository = this.svc.getSelected();
        this.selectedRefs = this.svc.getSelectedRefs();
        this.selectedTabId = this.svc.getSelectedTab();
        this.protectedReposFlag = isFeatureFlagEnabled(context.pageContext, "Git.ProtectedRepos", false);
        this.showCheckIssueMessageCard = isFeatureFlagEnabled(context.pageContext, FeatureFlagConstants.EnableChecksScalabilityResourceIssues, false);

        this.updateRequiredPermissions(this.selectedRepository.value);

        if (
            !this.selectedTabId.value ||
            (this.selectedTabId.value !== SettingsTabIds.permissionsMid &&
                this.selectedTabId.value !== SettingsTabIds.policiesMid &&
                this.selectedTabId.value !== SettingsTabIds.settingsMid)
        ) {
            // tab doesn't exist here so reset
            this.svc.setSelectedTab(SettingsTabIds.settingsMid, true);
        }
    }

    private updateRequiredPermissions = (selectedRepo?: IRepository, _action?: string) => {
        if (selectedRepo) {
            const repositoryPermissions: { [key in GitRepositoryPermissions]?: boolean } = {};
            const permisionChecks = this.checkedPermissions.map(permission =>
                this.svc.checkPermissionsAsync(permission, selectedRepo.id).then(state => {
                    repositoryPermissions[permission] = state;
                })
            );
            Promise.all(permisionChecks).then(() => {
                this.repositoryPermissions.value = repositoryPermissions;
            });
        }
    };

    private selectedRepositoryHasPermission = (Permission: GitRepositoryPermissions): boolean => {
        return this.repositoryPermissions.value[Permission] === true;
    };

    public componentDidMount = () => {
        super.componentDidMount();
        this.selectedRepository.subscribe(this.resetScroll);
        this.selectedRepository.subscribe(this.updateRequiredPermissions);
        this.selectedRefs.subscribe(this.resetScroll);
    };

    public componentWillUnmount = () => {
        super.componentWillUnmount();
        this.selectedRepository.unsubscribe(this.resetScroll);
        this.selectedRepository.unsubscribe(this.updateRequiredPermissions);
        this.selectedRefs.unsubscribe(this.resetScroll);
    };

    private renderHeader(): JSX.Element {
        return <Header className="details-header" titleSize={TitleSize.Large} title={this.getHeaderTitle()} commandBarItems={this.getCommandBarItems()} />;
    }

    private renderTabs(): JSX.Element {
        const settingsService = this.context.pageContext.getService<ISettingsViewService>("SettingsViewService");

        return (
            <TabBar selectedTabId={this.selectedTabId.value || SettingsTabIds.settingsMid} onSelectedTabChanged={this.onSelectedTabChanged}>
                <Tab
                    id={SettingsTabIds.settingsMid}
                    key={SettingsTabIds.settingsMid}
                    name={Resources.SettingsTab}
                    url={settingsService.getUrl(
                        this.selectedRepository.value && this.selectedRepository.value.id,
                        undefined,
                        SettingsTabIds.settingsMid
                    )}
                />
                {this.selectedRepository.value && !this.selectedRepository.value.isTfvc && (
                    <Tab
                        id={SettingsTabIds.policiesMid}
                        key={SettingsTabIds.policiesMid}
                        name={Resources.PoliciesTab}
                        url={settingsService.getUrl(
                            this.selectedRepository.value && this.selectedRepository.value.id,
                            undefined,
                            SettingsTabIds.policiesMid
                        )}
                    />
                )}
                <Tab
                    id={SettingsTabIds.permissionsMid}
                    key={SettingsTabIds.permissionsMid}
                    name={Resources.SecurityTab}
                    url={settingsService.getUrl(
                        this.selectedRepository.value && this.selectedRepository.value.id,
                        undefined,
                        SettingsTabIds.permissionsMid
                    )}
                />
                {this.selectedRepositoryHasPermission(GitRepositoryPermissions.EditPolicies) && (
                    <Tab
                        id={SettingsTabIds.checks}
                        key={SettingsTabIds.checks}
                        name={Resources.ApprovalsAndChecks}
                        url={settingsService.getUrl(
                            this.selectedRepository.value && this.selectedRepository.value.id,
                            undefined,
                            SettingsTabIds.checks
                        )}
                    />
                )}
            </TabBar>
        );
    }

    private renderPageContent() {
        const key = this.svc.getStateKey();
        if (
            this.selectedTabId.value == SettingsTabIds.checks
        ) {
            return (
                <RepositoriesChecksComponent
                    key={this.selectedRepository.value!.id}
                    repositoryId={this.svc.getProject().id + "." + this.selectedRepository.value!.id}
                    resourceName={this.selectedRepository.value!.name}
                    hasManagePermission={true}
                />
            );
        }

        if (this.selectedRepository.value && !this.selectedRepository.value.isTfvc && this.selectedTabId.value == SettingsTabIds.policiesMid) {
            return <RepositoryPolicies key={key} createChildLayer={this.props.createChildLayer} />;
        }

        if (this.selectedTabId.value == SettingsTabIds.permissionsMid) {
            return <RepositoryPermissions key={key} createChildLayer={this.props.createChildLayer} />;
        }

        return (
            <WrappedComponent
                key={key}
                loadingComponent={renderLoading}
                dependencies={["ms.vss-code-web.settings-options"]}
                wrappedProps={{
                    defaultBranch: this.selectedRepository.value!.defaultBranch,
                    projectId: this.svc.getProject().id,
                    repository: this.selectedRepository.value!.gitRepository,
                    isTfvc: this.selectedRepository.value!.isTfvc
                }}
                wrappedType="ms.vss-code-web.settings-options-component"
            />
        );
    }

    public render = (): JSX.Element => {
        return (
            <Page className="flex-grow" scrollableContainerRef={this.scrollRef}>
                <Observer
                    selected={this.selectedRepository}
                    selectedRefs={this.selectedRefs}
                    selectedTabId={this.selectedTabId}
                    showApprovalsAndChecks={this.showApprovalsAndChecks}
                    repositoryPermissions={this.repositoryPermissions}
                >
                    {(props: { selectedTabId: string | undefined; selectedRefs: string | undefined; showApprovalsAndChecks: boolean }) => {
                        return (
                            <>
                                {this.renderHeader()}
                                {this.showCheckIssueMessageCard && this._renderCheckIssuesMessageBar()}
                                {this.renderTabs()}
                                <div className="page-content page-content-top">{this.renderPageContent()}</div>
                            </>
                        );
                    }}
                </Observer>
            </Page>
        );
    };

    private _renderCheckIssuesMessageBar() {
        if(this.selectedRepository.value) {
            return (
                <WrappedComponent
                        dependencies = {["ms.vss-pipelinechecks.web"]}
                        resourceIds = { this.selectedRepository.value.id }
                        checkIssue = { this.selectedRepository.value?.issue }
                        complianceDeadline = {this.svc?.getCheckScalabilityComplianceDeadline()}
                        resourceTypeName = {ResourceTypeNames.Repository}
                        wrappedType="checks-issues-message"/>
            )
        }
    }

    private getCommandBarItems = (): IObservableArray<IHeaderCommandBarItem> | undefined => {
        if (this.selectedRefs.value) {
            return undefined;
        }

        const itemArr: IObservableArray<IHeaderCommandBarItem> = new ObservableArray([
            {
                href: this.selectedRepository.value!.webUrl,
                iconProps: {
                    iconName: "View"
                },
                id: "open",
                important: true,
                isPrimary: true,
                onActivate: (
                    menuItem: IMenuItem,
                    event?: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement> | undefined
                ) => {
                    onClickFPS(this.context.pageContext, this.selectedRepository.value!.webUrl, true, event);
                },
                text: Resources.GoTo
            }
        ]);

        if (this.selectedRepository.value && !this.selectedRepository.value.isTfvc) {
            const renameItem: IHeaderCommandBarItem = {
                iconProps: {
                    iconName: "Rename"
                },
                id: "rename",
                important: true,
                onActivate: () => {
                    this.svc.showDialog(DialogType.Rename, this.selectedRepository.value!);
                },
                text: Resources.Rename,
                disabled: !this.selectedRepositoryHasPermission(GitRepositoryPermissions.RenameRepository)
            };

            itemArr.push(renameItem);

            const deleteItem: IHeaderCommandBarItem = {
                iconProps: {
                    iconName: "Delete"
                },
                id: "delete",
                onActivate: () => {
                    this.svc.showDialog(DialogType.Delete, this.selectedRepository.value!);
                },
                text: Resources.Delete,
                disabled: !this.selectedRepositoryHasPermission(GitRepositoryPermissions.DeleteRepository),
                important: !this.protectedReposFlag // if the feature flag is enabled the option has to go in the more actions menu
            };

            itemArr.push(deleteItem);
        }

        return itemArr;
    };

    private onSelectedTabChanged = (newTabId: string) => {
        this.svc.setSelectedTab(newTabId);
        this.showApprovalsAndChecks.value = false;
    };

    private resetScroll = () => {
        if (this.scrollRef && this.scrollRef.current) {
            this.scrollRef.current.scrollTop = 0;
        }
    };

    private getHeaderTitle = () => {
        return this.selectedRefs.value ? getRefTitle(this.selectedRefs.value) : this.selectedRepository.value?.name;
    };

    private onBackButtonClicked = () => {
        this.showApprovalsAndChecks.value = false;
        this.svc.setSelectedTab(undefined, true);
    };
}

function getRefTitle(value: string): string {
    let result = refToBranchOrTagName(value);

    if (result.length < 1) {
        const ref = permissionsAdditionalData.find(x => x.ref === value);
        result = ref ? ref.displayText || "" : "";
    }

    return result;
}

function renderLoading() {
    return <Spinner size={SpinnerSize.large} />;
}
